import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import PageMeta from '@/components/feature/PageMeta';
import { BreadcrumbSchema, WebPageSchema } from '@/components/feature/SEOSchema';

const timeline = [
  { year: '2020', title: '网站创立', desc: '由一群热爱影视的资深影迷创立，旨在为网友提供可靠的在线看片网站导航服务。' },
  { year: '2021', title: '资源拓展', desc: '平台收录的看片网站突破100个，覆盖电影、剧集、综艺、动漫、纪录片五大品类。' },
  { year: '2022', title: '建立评测体系', desc: '推出专业的看片网站评测标准，从画质、速度、广告、资源、安全五大维度严格筛选。' },
  { year: '2023', title: '月活突破30万', desc: '凭借高质量的推荐内容，月活跃用户突破30万，成为中文领域最受欢迎的看片导航站之一。' },
  { year: '2024', title: '全面升级', desc: '网站全面改版升级，新增分类导航、智能筛选和用户评价功能，浏览体验大幅提升。' },
  { year: '2025', title: '持续精进', desc: '月活跃用户突破50万，持续更新推荐列表，确保每个推荐的看片网站都经过严格验证。' },
];

const reputations = [
  { name: '张先生', role: '影迷', text: '之前总是找不到清晰的电影资源，自从用了这个网站推荐，找片效率提高了好几倍。推荐的网站画质都很棒！', rating: 5 },
  { name: '李女士', role: '追剧达人', text: '追剧女孩的福音！推荐的剧集网站更新特别快，热播剧基本当晚就能看到，而且几乎没有广告骚扰。', rating: 5 },
  { name: '王同学', role: '动漫爱好者', text: '二次元爱好者强推！动漫分类做得特别细致，日漫新番几乎和日本同步更新，国漫资源也特别全。', rating: 5 },
  { name: '赵先生', role: '纪录片迷', text: '作为一个纪录片爱好者，这里的纪录片看片网站推荐太实用了。BBC、国家地理的资源应有尽有。', rating: 4 },
  { name: '刘女士', role: '综艺粉', text: '综艺节目的更新速度让我惊喜，热门卫视综艺基本实时同步，再也不用担心错过精彩内容了。', rating: 5 },
  { name: '陈先生', role: '影视从业者', text: '作为业内人士，我很认可这个网站的筛选标准。推荐的看片网站确实都是当前体验最好的，专业度很高。', rating: 5 },
];

export default function AboutPage() {
  const [formState, setFormState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormState('loading');
    setFormError('');

    const form = e.currentTarget;
    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setFormState('success');
      form.reset();
      return;
    }

    try {
      const formData = new FormData(form);
      if (honeypotInput) {
        formData.delete('website_alt');
      }
      const body = new URLSearchParams(formData as unknown as Record<string, string>).toString();

      const response = await fetch('https://readdy.ai/api/form/d9u41k5ca23r0tr1aq2g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormState('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormState('error');
        setFormError(serverMsg.includes('spam') || serverMsg.includes('form data is spam')
          ? '提交失败，请稍后再试。'
          : (typeof serverMsg === 'string' && serverMsg.length > 0 ? serverMsg : '提交失败，请稍后再试。'));
      }
    } catch {
      setFormState('error');
      setFormError('网络错误，请检查网络后重试。');
    }
  };

  return (
    <>
      <PageMeta
        title="关于我们 - 看片网站推荐2025"
        description="看片网站推荐致力于为广大网友筛选优质的免费在线看片网站，涵盖电影、剧集、综艺、动漫、纪录片等品类，提供专业的评测和导航服务。"
        keywords="关于我们,看片网站推荐,品牌介绍,联系我们"
        canonicalUrl="https://wfsqdz.cn/about"
        ogTitle="关于我们 - 看片网站推荐2025"
        ogDescription="看片网站推荐致力于为广大网友筛选优质的免费在线看片网站。"
      />
      <WebPageSchema
        title="关于我们 - 看片网站推荐2025"
        description="看片网站推荐致力于为广大网友筛选优质的免费在线看片网站。"
        url="https://wfsqdz.cn/about"
      />
      <BreadcrumbSchema
        items={[
          { name: '首页', url: 'https://wfsqdz.cn/' },
          { name: '关于我们', url: 'https://wfsqdz.cn/about' },
        ]}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <section className="bg-background-100/60 py-16 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8" aria-label="面包屑导航">
                <Link to="/" className="hover:text-foreground-700 transition-colors">首页</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-800 font-medium">关于我们</span>
              </nav>

              <div className="max-w-3xl mx-auto text-center">
                <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-4">
                  关于我们
                </span>
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
                  关于看片网站推荐
                </h1>
                <p className="text-base md:text-lg text-foreground-500 leading-relaxed">
                  我们是一群热爱影视的资深影迷，深知找片难的痛苦。
                  因此，我们创建了这个平台，只为帮助更多像你一样热爱观影的朋友，
                  快速找到清晰流畅、无广告干扰的优质看片网站。
                </p>
              </div>
            </div>
          </section>

          <section className="py-16 md:py-20">
            <div className="max-w-5xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  我们的使命
                </h2>
                <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto">
                  用心筛选每一个看片网站，让观影变得更简单、更纯粹
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  { icon: 'ri-shield-check-line', title: '安全可靠', desc: '每个推荐网站都经过严格的安全审核，确保不含恶意代码和病毒，让您安心观影。' },
                  { icon: 'ri-thumb-up-line', title: '真实推荐', desc: '所有推荐均基于真实使用体验，拒绝付费推广，只推荐真正好用的看片网站。' },
                  { icon: 'ri-loop-left-line', title: '持续更新', desc: '团队每日监控网站状态，及时更新推荐列表，确保推荐的每个网站都能正常访问。' },
                ].map((item) => (
                  <div key={item.title} className="text-center p-8 bg-background-50 border border-background-200/70 rounded-xl">
                    <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-primary-100 mx-auto mb-5">
                      <i className={`${item.icon} text-2xl text-primary-600`}></i>
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">{item.title}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-16 md:py-20 bg-background-100/60">
            <div className="max-w-5xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  发展历程
                </h2>
                <p className="text-sm md:text-base text-foreground-500">从创立到壮大，每一步都踏实前行</p>
              </div>

              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px"></div>
                <div className="space-y-8">
                  {timeline.map((item, index) => (
                    <div key={item.year} className={`relative flex items-start gap-6 md:gap-10 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                      <div className="md:w-1/2 flex-shrink-0" />
                      <div className="absolute left-4 md:left-1/2 w-8 h-8 rounded-full bg-accent-500 border-4 border-background-50 -translate-x-1/2 flex items-center justify-center z-10">
                        <span className="text-[10px] font-bold text-background-50">{item.year.slice(2)}</span>
                      </div>
                      <div className="md:w-1/2 ml-10 md:ml-0 pl-6 md:pl-0">
                        <div className={`bg-background-50 border border-background-200/70 rounded-xl p-5 ${index % 2 === 0 ? 'md:pr-10' : 'md:pl-10'}`}>
                          <span className="text-xs font-bold text-accent-600">{item.year}</span>
                          <h3 className="font-heading text-base font-semibold text-foreground-900 mt-1 mb-2">{item.title}</h3>
                          <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section id="reputation" className="py-16 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-4">
                  用户口碑
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  听听用户怎么说
                </h2>
                <p className="text-sm md:text-base text-foreground-500">来自真实用户的使用反馈</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {reputations.map((rep) => (
                  <div key={rep.name} className="bg-background-50 border border-background-200/70 rounded-xl p-6">
                    <div className="flex items-center gap-1 mb-3">
                      {[...Array(5)].map((_, i) => (
                        <i
                          key={i}
                          className={`text-xs ${i < rep.rating ? 'ri-star-fill text-secondary-500' : 'ri-star-line text-foreground-300'}`}
                        ></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">&ldquo;{rep.text}&rdquo;</p>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                        <span className="text-sm font-bold text-primary-600">{rep.name[0]}</span>
                      </div>
                      <div>
                        <span className="text-sm font-semibold text-foreground-800 block">{rep.name}</span>
                        <span className="text-xs text-foreground-400">{rep.role}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section id="contact" className="py-16 md:py-20 bg-background-100/60">
            <div className="max-w-2xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold mb-4">
                  联系我们
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  与我们取得联系
                </h2>
                <p className="text-sm md:text-base text-foreground-500">
                  有任何问题或建议？欢迎给我们留言，我们会尽快回复
                </p>
              </div>

              <form
                className="bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8 space-y-5"
                onSubmit={handleSubmit}
                data-readdy-form
              >
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="anti-spam-field"
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-2">
                      姓名 <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-2">
                      邮箱 <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-2">
                    主题
                  </label>
                  <input
                    id="contact-subject"
                    name="subject"
                    type="text"
                    className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="请输入主题"
                  />
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-2">
                    留言内容 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                    placeholder="请输入您的留言内容（500字以内）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                </div>

                {formState === 'success' && (
                  <div className="p-4 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700">
                    <i className="ri-check-line mr-2"></i>提交成功！感谢您的留言，我们会尽快回复。
                  </div>
                )}
                {formState === 'error' && (
                  <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                    <i className="ri-error-warning-line mr-2"></i>{formError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={formState === 'loading'}
                  className="w-full py-3.5 bg-primary-500 text-background-50 font-semibold rounded-lg hover:bg-primary-600 transition-all duration-200 text-sm disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {formState === 'loading' ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i> 提交中...
                    </span>
                  ) : '发送留言'}
                </button>
              </form>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}